import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EidAuthenticate} from 'src/components/authentication/EidAuthenticate';
import {AuthenticatedPeopleList} from 'src/components/clinic/booking/people-select/components/AuthenticatedPeople';
import {Button} from 'src/components/form/button/Button';
import {Checkbox} from 'src/components/form/checkbox/Checkbox';
import {Input} from 'src/components/form/input/Input';
import {Radio} from 'src/components/form/radio/Radio';
import ModalPortal from 'src/components/layout/modal/ModalPortal';
import {DataActions, Person, useArrivalContext} from 'src/context/ArrivalContext';
import {User} from 'src/context/BookingContext';
import {useSessionContext} from 'src/context/SessionContext';
import {fetchClinicById} from 'src/services/api/api';
import {ClinicJSON} from 'src/services/api/types/types';
import {getSystemCountry} from 'src/services/i18n/locale';
import {isOnlyMyself} from 'src/services/user/profile';
import {validateEmail, validatePhone} from 'src/services/validation/validation';

const systemCountry = getSystemCountry();

export function DropinEidPeople() {
    const {t} = useTranslation();
    const {
        dataState: {settings, persons, clinicId, reasonState},
        dispatch
    } = useArrivalContext();
    const selectedPersonsIds = persons.map((p) => p.identity);
    const {sessionState, startSession} = useSessionContext();
    const [policyConsent, setPolicyConsent] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [clinic, setClinic] = useState<ClinicJSON | null>(null);

    const numberPatients = reasonState.numberPatients ?? 1;
    const requireContactInfo = settings?.requireContactInfo || false;

    const minAge = reasonState.allowedAge?.minAge || 0;
    const maxAge = reasonState.allowedAge?.maxAge || 1000;

    const isWithinAgeRange = (age: number): boolean => {
        return minAge <= age && maxAge >= age;
    };

    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [marketing, setMarketing] = useState<Boolean | null>(null);

    const isPhoneValid = validatePhone(phone);
    const isEmailValid = validateEmail(email);

    const peopleValid =
        selectedPersonsIds.length === numberPatients &&
        (!requireContactInfo || (isPhoneValid && isEmailValid && phone.length > 0));

    function selectPerson(id: string) {
        if (selectedPersonsIds.includes(id)) {
            // remove me
            dispatch({type: DataActions.SET_PEOPLE, value: persons.filter((p) => p.identity !== id)});
            return;
        }

        const contactInfo = {
            mobile: phone,
            email: email,
            marketing: marketing
        } as any;

        // add me

        const newPersons = [...persons];
        if (sessionState.user?.identity === id) {
            newPersons.push({
                id: 'me',
                identity: sessionState.user.identity,
                identityValid: true,
                givenName: sessionState.user.givenName,
                familyName: sessionState.user.familyName,
                age: sessionState.user.age,
                gender: sessionState.user.gender === 'male' ? 'male' : 'female',
                contactInfo: requireContactInfo ? contactInfo : undefined
            });
        }

        if (sessionState.user?.relations) {
            const found = sessionState.user?.relations.find((u) => u.identity === id);
            if (found) {
                newPersons.push({
                    id: found.id,
                    identity: found.identity,
                    identityValid: true,
                    givenName: found.givenName,
                    familyName: found.familyName,
                    age: found.age,
                    gender: found.gender === 'male' ? 'male' : 'female',
                    contactInfo: requireContactInfo ? contactInfo : undefined
                });
            }
        }

        dispatch({type: DataActions.SET_PEOPLE, value: newPersons});
    }

    const isDisabledUser = (user: User) => {
        return (
            (selectedPersonsIds.length === numberPatients && !selectedPersonsIds.includes(user.identity)) ||
            !isWithinAgeRange(user.age || 0)
        );
    };

    const handleUpdateContacts = (value: any) => {
        const newPersons = persons.map((p) => {
            return {
                ...p,
                contactInfo: value
            } as Person;
        });
        dispatch({type: DataActions.SET_PEOPLE, value: newPersons});
    };

    useEffect(() => {
        if (!showModal || clinic !== null) {
            return;
        }

        const fetchClinic = async () => {
            const data = await fetchClinicById(clinicId);
            setClinic(data);
        };

        fetchClinic();
    }, [showModal, clinicId, clinic]);

    useEffect(() => {
        if (
            sessionState.hasActiveSession &&
            sessionState.user &&
            isWithinAgeRange(sessionState.user.age || 0) &&
            isOnlyMyself(sessionState.user)
        ) {
            selectPerson(sessionState.user.identity);
        }
    }, [sessionState.user]);
    return (
        <div>
            {!sessionState.hasActiveSession && (
                <>
                    <h1 className='main-content__header'>{t('arrival.identify_yourself')}</h1>
                    <p style={{marginBottom: '2rem'}}>{t('arrival.identify_yourself_explanation')}</p>
                    <EidAuthenticate
                        onLoginSuccess={({jwt, sessionDuration, tokenDuration}) => {
                            startSession(jwt, sessionDuration, tokenDuration);
                        }}
                    />
                </>
            )}
            {sessionState.hasActiveSession && (
                <>
                    <h1 className='main-content__header'>{t('arrival.people_select')}</h1>
                    <p>{t('arrival.people_select_count', {count: numberPatients})}</p>
                    <p style={{marginBottom: '2rem'}}>{t('arrival.people_select_explanation')}</p>
                    <AuthenticatedPeopleList
                        loading={!sessionState.user}
                        user={sessionState.user ?? null}
                        selfBookingOnly={settings?.eidSelfOnly === true}
                        selectedPersonsIds={selectedPersonsIds}
                        selectPerson={selectPerson}
                        validateUser={isDisabledUser}
                        ageRange={[minAge || null, maxAge || null]}
                    />
                    {requireContactInfo && (
                        <div className='person-form-container'>
                            <div className='people-form__name-container--name-input'>
                                <Input
                                    type='tel'
                                    id={`phone-input`}
                                    isValid={isPhoneValid}
                                    required
                                    label={t('confirm.form.telephone')}
                                    value={phone}
                                    onChange={(val) => {
                                        setPhone(val);
                                        handleUpdateContacts({
                                            mobile: val,
                                            email: email,
                                            marketing: marketing
                                        });
                                    }}
                                />
                                {!isPhoneValid && (
                                    <p className='input-error'>{t(`confirm.form.phone_error.${systemCountry}`)}</p>
                                )}
                            </div>
                            <div className='people-form__name-container--name-input'>
                                <Input
                                    type='email'
                                    id={`email-input`}
                                    isValid={isEmailValid}
                                    label={t('confirm.form.email')}
                                    value={email}
                                    onChange={(val) => {
                                        setEmail(val);
                                        handleUpdateContacts({
                                            mobile: phone,
                                            email: val,
                                            marketing: marketing
                                        });
                                    }}
                                />
                                {!isEmailValid && <p className='input-error'>{t('confirm.form.email_error')}</p>}
                            </div>
                            <div>
                                <div>
                                    {t('confirm.policy.storing_data_non_optional')}
                                    <div className='radio-group' role='radiogroup'>
                                        <Radio
                                            checked={marketing === true}
                                            name={`consent`}
                                            label={t('consent.yes')}
                                            onClick={() => {
                                                handleUpdateContacts({
                                                    mobile: phone,
                                                    email: email,
                                                    marketing: true
                                                });
                                                setMarketing(true);
                                            }}
                                        />
                                        <Radio
                                            checked={marketing === false}
                                            name={`consent`}
                                            label={t('consent.no')}
                                            onClick={() => {
                                                handleUpdateContacts({
                                                    mobile: phone,
                                                    email: email,
                                                    marketing: false
                                                });
                                                setMarketing(false);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div style={{paddingTop: '30px'}}>
                                <Checkbox
                                    onClick={(bool) => {
                                        setPolicyConsent(bool);
                                    }}
                                    id='policyConsent'
                                    checked={policyConsent}
                                    ariaLabel={`${t('confirm.policy.personal_data')} ${t('confirm.policy.personal_data_policy')}`}
                                    jsx={
                                        <>
                                            <span>{t('confirm.policy.personal_data')}</span>
                                            &nbsp;
                                            <Button
                                                variant='text'
                                                className='in-text-link'
                                                onClick={() => setShowModal(true)}
                                            >
                                                <span>{t('confirm.policy.personal_data_policy')}</span>
                                            </Button>
                                        </>
                                    }
                                />
                            </div>
                        </div>
                    )}
                    {showModal && (
                        <ModalPortal onCloseClick={() => setShowModal(false)}>
                            <div className='policy-modal'>
                                <h1 className='policy-modal__header'>{t('confirm.policy.header')}</h1>
                                <p className='policy-modal__content wrap-html'>{clinic?.termsText}</p>
                            </div>
                        </ModalPortal>
                    )}
                </>
            )}

            <div className='arrival-step-controllers'>
                <div>
                    <Button
                        onClick={() => {
                            dispatch({type: DataActions.SET_PEOPLE, value: []});
                            dispatch({type: DataActions.SET_STEP, value: 'initial'});
                        }}
                    >
                        {t('arrival.back')}
                    </Button>
                </div>
                <div>
                    <Button
                        onClick={() => {
                            dispatch({type: DataActions.SET_PEOPLE, value: persons});
                            dispatch({type: DataActions.NEXT_STEP});
                        }}
                        disabled={peopleValid === false || policyConsent === false || marketing === null}
                    >
                        {t('arrival.next')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
